import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createChart, ColorType, IChartApi, ISeriesApi, BaselineSeries, LineType } from 'lightweight-charts';
import dayjs from 'dayjs';

export interface PnlSnapshot {
    datetime?: string;
    holder_usd?: number;
    liquidity_usd?: number;
    fee_usd?: number;
}

interface PeriodReturnChartProps {
    snapshots: PnlSnapshot[];
    height?: number;
}

type CurrencyMode = 'USD' | 'Fee';

export const PeriodReturnChart: React.FC<PeriodReturnChartProps> = ({ snapshots, height = 400 }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const seriesRef = useRef<ISeriesApi<"Baseline"> | null>(null);
    const [rangeStats, setRangeStats] = useState({ startTime: '', endTime: '', value: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('USD');

    const chartData = useMemo(() => {
        if (!snapshots || snapshots.length === 0) return [];

        const parsedSnapshots = snapshots.map(snap => {
            const dt = dayjs(snap.datetime || '');
            return {
                dt,
                holderUsd: snap.holder_usd ?? 0,
                liquidityUsd: snap.liquidity_usd ?? 0,
                feeUsd: snap.fee_usd ?? 0,
            };
        }).sort((a, b) => a.dt.valueOf() - b.dt.valueOf());

        let firstLiquidityUsd = parsedSnapshots[0].liquidityUsd;
        const aggregatedData = [];
        let currentGroupStart = null;
        let currentGroupLiquidityUsdBefore = 0;
        let feeUsdStart = 0;
        let feeUsdEnd = 0;

        for (let i = 0; i < parsedSnapshots.length; i++) {
            const snap = parsedSnapshots[i];

            if (!currentGroupStart) {
                currentGroupStart = snap.dt;
                currentGroupLiquidityUsdBefore = snap.liquidityUsd;
                feeUsdStart = snap.feeUsd;
                feeUsdEnd = snap.feeUsd;
                continue;
            }

            const currentGroupEnd = snap.dt;
            const currentGroupLiquidityUsdAfter = snap.liquidityUsd;
            feeUsdEnd = snap.feeUsd;

            const durationSeconds = currentGroupEnd.diff(currentGroupStart, 'second');

            if (durationSeconds >= 600 || i === parsedSnapshots.length - 1) {
                let valueUsd = (currentGroupLiquidityUsdAfter) / currentGroupLiquidityUsdBefore - 1;
                let valueFee = (feeUsdEnd - feeUsdStart) / currentGroupLiquidityUsdBefore;

                // We use the end of the period as the data point time
                const dataPoint = {
                    time: currentGroupStart.unix() as any,
                    hourlyRateUsd: (valueUsd * 100 * 3600 / Math.max(durationSeconds, 1)),
                    valueUsd: valueUsd * 100,
                    startTime: currentGroupStart,
                    endTime: currentGroupEnd,
                    liquidityUsdBefore: currentGroupLiquidityUsdBefore,
                    liquidityUsdAfter: currentGroupLiquidityUsdAfter,
                    valueFee: valueFee * 100,
                    feeUsdStart: feeUsdStart,
                    feeUsdEnd: feeUsdEnd,
                    hourlyRateFee: (valueFee * 100 * 3600 / Math.max(durationSeconds, 1)),
                };
                aggregatedData.push(dataPoint);

                // If it's the last point, also push the end time to close the step
                if (i === parsedSnapshots.length - 1) {
                    aggregatedData.push({
                        ...dataPoint,
                        time: currentGroupEnd.unix() as any,
                    });
                }

                currentGroupStart = snap.dt;
                currentGroupLiquidityUsdBefore = snap.liquidityUsd;
                feeUsdStart = snap.feeUsd;
                feeUsdEnd = snap.feeUsd;
            }
        }
        return aggregatedData;
    }, [snapshots]);

    const chartSeriesData = useMemo(() => {
        if (chartData.length === 0) return [];
        return chartData.map(d => ({
            time: d.time,
            value: currencyMode === 'USD' ? d.valueUsd : d.valueFee
        }));
    }, [chartData, currencyMode]);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: '#94a3b8',
            },
            grid: {
                vertLines: { color: 'rgba(255, 255, 255, 0.05)' },
                horzLines: { color: 'rgba(255, 255, 255, 0.05)' },
            },
            width: chartContainerRef.current.clientWidth,
            height: height,
            timeScale: {
                borderColor: 'rgba(255, 255, 255, 0.1)',
                timeVisible: true,
                secondsVisible: false,
            },
            rightPriceScale: {
                borderColor: 'rgba(255, 255, 255, 0.1)',
            },
        });

        const series = chart.addSeries(BaselineSeries, {
            baseValue: { type: 'price', price: 0 },

            topLineColor: '#10b981',
            topFillColor1: 'rgba(16, 185, 129, 0.4)',
            topFillColor2: 'rgba(16, 185, 129, 0.05)',

            bottomLineColor: '#ef4444',
            bottomFillColor1: 'rgba(239, 68, 68, 0.05)',
            bottomFillColor2: 'rgba(239, 68, 68, 0.4)',

            lineWidth: 2,
            lineType: LineType.WithSteps,
            priceFormat: {
                type: 'custom',
                formatter: (price: number) => `${price.toFixed(2)}%`,
            },
        });

        chartRef.current = chart;
        seriesRef.current = series;

        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({ width: chartContainerRef.current.clientWidth });
            }
        };

        window.addEventListener('resize', handleResize);

        // Subscribe to visible logical range change to update stats
        chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
            if (!range || chartData.length === 0) return;

            const visibleData = chartData.filter((d) => {
                const logicalRange = chart.timeScale().getVisibleLogicalRange();
                if (!logicalRange) return true;
                const time = d.time;
                const coordinate = chart.timeScale().timeToCoordinate(time);
                return coordinate !== null && coordinate >= 0 && coordinate <= chartContainerRef.current!.clientWidth;
            });

            if (visibleData.length > 0) {
                const first = visibleData[0];
                const last = visibleData[visibleData.length - 1];

                let cumulativeReturn = 0;
                if (currencyMode === 'USD') {
                    cumulativeReturn = (last.liquidityUsdAfter / first.liquidityUsdBefore - 1) * 100;
                } else {
                    cumulativeReturn = ((last.feeUsdEnd - first.feeUsdStart) / first.liquidityUsdBefore) * 100;
                }

                setRangeStats({
                    startTime: first.startTime.format('MM/DD HH:mm'),
                    endTime: last.endTime.format('MM/DD HH:mm'),
                    value: cumulativeReturn
                });
            }
        });

        // Custom Tooltip Logic
        chart.subscribeCrosshairMove((param: any) => {
            if (
                param.point === undefined ||
                !param.time ||
                param.point.x < 0 ||
                param.point.x > chartContainerRef.current!.clientWidth ||
                param.point.y < 0 ||
                param.point.y > height
            ) {
                if (tooltipRef.current) tooltipRef.current.style.display = 'none';
            } else {
                const data = param.seriesData.get(series);
                if (!data || !tooltipRef.current) {
                    if (tooltipRef.current) tooltipRef.current.style.display = 'none';
                    return;
                }

                tooltipRef.current.style.display = 'block';

                // Find matching point in chartData
                // Since points are intervals, we find the first data point with d.time <= param.time
                const pointData = [...chartData].reverse().find(d => d.time <= (param.time as number));
                if (!pointData) return;

                const durationStr = pointData.startTime ?
                    `${Math.round(pointData.endTime.diff(pointData.startTime, 'minute'))}m` :
                    '-';

                const val = currencyMode === 'USD' ? pointData.valueUsd : pointData.valueFee;
                const hourlyRate = currencyMode === 'USD' ? pointData.hourlyRateUsd : pointData.hourlyRateFee;

                tooltipRef.current.innerHTML = `
                    <div style="color: #64748b; font-size: 11px; margin-bottom: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">Period Return (${currencyMode})</div>
                    <div style="color: #f1f5f9; font-size: 13px; margin-bottom: 4px;">Time: <span style="font-family: 'JetBrains Mono', monospace; font-weight: 600;">${pointData.startTime.format('HH:mm')} - ${pointData.endTime.format('HH:mm')}</span></div>
                    <div style="color: #94a3b8; font-size: 13px; margin-bottom: 4px;">Duration: <span style="font-weight: 600;">${durationStr}</span></div>
                    <div style="color: ${val >= 0 ? '#10b981' : '#ef4444'}; font-size: 13px; font-weight: bold; margin-bottom: 4px;">Abs Return: ${val >= 0 ? '+' : ''}${val.toFixed(4)}%</div>
                    <div style="color: #6366f1; font-size: 13px; font-weight: bold;">Hourly Rate: ${val >= 0 ? '+' : ''}${hourlyRate.toFixed(4)}%</div>
                `;

                const toolTipWidth = 180;
                const toolTipHeight = 110;
                const toolTipMargin = 15;

                let left = param.point.x + toolTipMargin;
                if (left > chartContainerRef.current!.clientWidth - toolTipWidth) {
                    left = param.point.x - toolTipMargin - toolTipWidth;
                }

                let top = param.point.y + toolTipMargin;
                if (top > height - toolTipHeight) {
                    top = param.point.y - toolTipMargin - toolTipHeight;
                }

                tooltipRef.current.style.left = left + 'px';
                tooltipRef.current.style.top = top + 'px';
            }
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
        };
    }, [height, currencyMode]); // Recreate chart when mode changes to bind new event listeners with correct closure

    useEffect(() => {
        if (seriesRef.current && chartSeriesData.length > 0) {
            seriesRef.current.setData(chartSeriesData);
            chartRef.current?.timeScale().fitContent();
        }
    }, [chartSeriesData]);

    return (
        <div style={{ width: '100%' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '1rem',
                padding: '1.25rem',
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(8px)',
                borderRadius: '16px',
                marginBottom: '1.5rem',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                alignItems: 'center'
            }}>
                <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Visible Time Range</div>
                    <div style={{ fontSize: '1.1rem', color: '#f1f5f9', fontWeight: '600', fontFamily: 'JetBrains Mono, monospace' }}>
                        {rangeStats.startTime || '-'} — {rangeStats.endTime || '-'}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        display: 'flex',
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '8px',
                        padding: '2px',
                        border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                        {(['USD', 'Fee'] as CurrencyMode[]).map((mode) => (
                            <button
                                key={mode}
                                onClick={() => setCurrencyMode(mode)}
                                style={{
                                    background: currencyMode === mode ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                                    color: currencyMode === mode ? '#818cf8' : '#64748b',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '4px 12px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    borderLeft: mode === 'Fee' ? '1px solid rgba(255,255,255,0.05)' : 'none'
                                }}
                            >
                                {mode}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>Period Return ({currencyMode})</div>
                    <div style={{
                        fontSize: '1.4rem',
                        fontWeight: '800',
                        fontFamily: 'JetBrains Mono, monospace',
                        color: rangeStats.value >= 0 ? '#10b981' : '#ef4444',
                        textShadow: rangeStats.value >= 0 ? '0 0 15px rgba(16, 185, 129, 0.3)' : '0 0 15px rgba(239, 68, 68, 0.3)'
                    }}>
                        {rangeStats.value >= 0 ? '+' : ''}{rangeStats.value.toFixed(4)}%
                    </div>
                </div>
            </div>

            <div
                ref={chartContainerRef}
                className="lightweight-chart-container"
                style={{
                    width: '100%',
                    height,
                    position: 'relative',
                    background: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: '16px',
                    padding: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.03)'
                }}
            >
                <div ref={tooltipRef} style={{
                    display: 'none',
                    position: 'absolute',
                    zIndex: 1000,
                    padding: '12px',
                    background: 'rgba(15, 23, 42, 0.85)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    pointerEvents: 'none',
                    transition: 'opacity 0.2s ease'
                }} />
            </div>

            <div style={{
                marginTop: '10px',
                fontSize: '0.75rem',
                color: '#475569',
                textAlign: 'right',
                fontStyle: 'italic'
            }}>
                * Values represent annualized performance for sub-periods and cumulative for the selected range.
            </div>
        </div>
    );
};
